import { useCallback, useEffect, useState } from "react";
import { Loader2, TrendingDown, AlertTriangle, Layers, ShoppingCart, Receipt,
  Lock } from "lucide-react";
import { toast } from "sonner";
import { opsApi, money, STATUS_TONE } from "./lib/api";

const TONE = { bad: "border-rose-200 bg-rose-50 text-rose-800",
  warn: "border-amber-200 bg-amber-50 text-amber-800" };

const Stat = ({ label, value, sub, tid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={tid}>
    <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
    <p className="stat-num text-2xl font-semibold text-slate-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
  </div>
);

/** Operations, first tab: what is live, what needs a person today, and what group buying has
 *  saved us this year. */
export default function OpsDashboardPage() {
  const [d, setData] = useState(null);
  const [rec, setRec] = useState(null);
  const [ret, setRet] = useState(null);

  const load = useCallback(async () => {
    try { setData(await opsApi.dashboard()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the dashboard"); }
    try {
      const [receivables, retainage] = await Promise.all([
        opsApi.receivables(), opsApi.retainageWatch(),
      ]);
      setRec(receivables);
      setRet(retainage);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the money views"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading the operations dashboard…
    </div>;
  }
  const s = d.savings;
  const top = s.by_manufacturer[0];
  const max = Math.max(...s.by_manufacturer.map((m) => m.saving), 1);

  return (
    <div className="page-body space-y-6" data-testid="ops-dashboard-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Live jobs" value={d.portfolio.live} tid="ops-dash-live"
              sub="Estimate, Ready and Open" />
        <Stat label="Live contract value" value={money(d.portfolio.live_value)}
              tid="ops-dash-live-value" />
        <Stat label="Needs a person today" value={d.attention.length}
              tid="ops-dash-attention-count" />
        <Stat label={`Group buying saved in ${s.year}`} value={money(s.total_saving)}
              tid="ops-dash-savings-total"
              sub={s.budgeted ? `${s.saving_pct}% of ${money(s.budgeted)} bought` : "No group orders placed yet"} />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ops-dash-portfolio">
        {d.portfolio.statuses.map((st) => (
          <span key={st.value} title={st.meaning}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${STATUS_TONE[st.value] || ""}`}>
            {st.label} · {d.portfolio.counts[st.value] || 0}
          </span>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                 data-testid="ops-dash-attention">
          <h2 className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-900">
            Needs a person today
          </h2>
          {d.attention.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              Nothing is overdue, over budget or sliding. Every live job is where it should be.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.attention.map((a, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-2 text-xs"
                    data-testid={`ops-dash-attention-${i}`}>
                  <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${
                    a.tone === "bad" ? "text-rose-500" : "text-amber-500"}`} />
                  <span className="w-16 shrink-0 font-medium text-slate-700">
                    {a.job_number || "—"}
                  </span>
                  <span className="w-40 shrink-0 truncate text-slate-500">{a.name}</span>
                  <span className={`truncate rounded-full border px-2 py-0.5 ${TONE[a.tone] || ""}`}>
                    {a.what}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                 data-testid="ops-dash-scoreboard">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <TrendingDown className="h-4 w-4 text-emerald-600" />
            <h2 className="text-sm font-semibold text-slate-900">
              Savings Scoreboard · {s.year}
            </h2>
            <span className="ml-auto text-xs text-slate-500">
              {s.group_orders} group order(s)
            </span>
          </div>
          {s.by_manufacturer.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              No group orders placed this year yet. Build one in Purchasing › Group Orders and the
              saving lands here.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-slate-100">
                {s.by_manufacturer.map((m) => (
                  <li key={m.manufacturer} className="px-4 py-2"
                      data-testid={`ops-savings-${m.manufacturer}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="w-44 shrink-0 truncate font-medium text-slate-800">
                        {m.manufacturer}
                      </span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <span className="block h-full rounded-full bg-emerald-500"
                              style={{ width: `${Math.round(m.saving / max * 100)}%` }} />
                      </span>
                      <span className="w-24 shrink-0 text-right font-semibold text-emerald-700">
                        {money(m.saving)}
                      </span>
                    </div>
                    <p className="mt-0.5 pl-[11.5rem] text-[11px] text-slate-400">
                      {m.saving_pct}% of {money(m.budgeted)} · {m.groups} order(s) ·
                      {" "}{m.jobs} job placement(s)
                    </p>
                  </li>
                ))}
              </ul>
              <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
                {top ? `${top.manufacturer} is the best buy of the year. ` : ""}{s.note}
              </p>
            </>
          )}
        </section>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                 data-testid="ops-dash-receivables">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <Receipt className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-900">Aged Receivables</h2>
            <span className="ml-auto text-xs text-slate-500">
              {rec ? `${money(rec.total)} out · ${money(rec.past_due)} past due` : "…"}
            </span>
          </div>
          {rec && rec.rows.length > 0 && (
            <div className="flex gap-1 px-4 py-2 text-[11px]">
              {[["current", "0–30"], ["31_60", "31–60"], ["61_90", "61–90"],
                ["over_90", "90+"]].map(([k, label]) => (
                <span key={k} data-testid={`ops-receivables-bucket-${k}`}
                      className={`rounded-full border px-2 py-0.5 ${
                        k === "over_90" && rec.buckets[k] > 0
                          ? "border-rose-200 bg-rose-50 text-rose-700"
                          : k === "61_90" && rec.buckets[k] > 0
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-slate-200 text-slate-600"}`}>
                  {label} days · {money(rec.buckets[k])}
                </span>
              ))}
            </div>
          )}
          {!rec || rec.rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              Nothing is sitting unpaid. Every application has landed.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rec.rows.map((r) => (
                <li key={r.pay_application_id}
                    data-testid={`ops-receivable-${r.number}`}
                    className="flex flex-wrap items-center gap-2 px-4 py-2 text-xs">
                  <span className="w-14 shrink-0 font-medium text-slate-700">
                    {r.job_number || "—"}
                  </span>
                  <span className="w-36 shrink-0 truncate text-slate-500">{r.project}</span>
                  <span className="w-24 shrink-0 font-mono text-slate-700">{r.number}</span>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 ${
                    r.tone === "bad" ? "border-rose-200 bg-rose-50 text-rose-700"
                      : r.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-800"
                        : "border-slate-200 text-slate-600"}`}>{r.line}</span>
                  <span className="ml-auto font-semibold tabular-nums text-slate-900">
                    {money(r.payment_due)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {rec?.rows.length > 0 && (
            <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
              {rec.note}
            </p>
          )}
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                 data-testid="ops-dash-retainage">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <Lock className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-900">Retainage Watch</h2>
            <span className="ml-auto text-xs text-slate-500">
              {ret ? `${money(ret.total)} still theirs` : "…"}
            </span>
          </div>
          {!ret || ret.rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              No finished job is still holding retainage.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {ret.rows.map((r) => (
                <li key={r.project_id} data-testid={`ops-retainage-${r.job_number}`}
                    className="flex flex-wrap items-center gap-2 px-4 py-2 text-xs">
                  <span className="w-14 shrink-0 font-medium text-slate-700">
                    {r.job_number || "—"}
                  </span>
                  <span className="w-36 shrink-0 truncate text-slate-500">{r.project}</span>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 ${
                    r.tone === "bad" ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-amber-200 bg-amber-50 text-amber-800"}`}>{r.line}</span>
                  <span className="ml-auto font-semibold tabular-nums text-slate-900">
                    {money(r.retainage_held)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {ret?.rows.length > 0 && (
            <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
              {ret.note}
            </p>
          )}
        </section>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-4"
                 data-testid="ops-dash-purchasing">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-900">Purchasing</h2>
          </div>
          <p className="mt-2 text-xs text-slate-600">
            {d.purchasing.needs_attention} purchase order(s) need chasing ·
            {" "}{d.purchasing.awaiting_ack} awaiting acknowledgement ·
            {" "}{money(d.purchasing.committed)} committed
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4"
                 data-testid="ops-dash-groups-due">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-900">Group buys ready now</h2>
          </div>
          {d.group_orders_due.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500">Nothing groupable across the jobs today.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {d.group_orders_due.map((g) => (
                <li key={g.manufacturer}>
                  <b>{g.manufacturer}</b> · {g.jobs} jobs · {money(g.value)} ·
                  {" "}<span className="text-emerald-700">{money(g.projected_saving)} projected</span>
                  {g.send_by ? ` · send by ${g.send_by}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
